import { EV_PAYMENT_STATUS_LABELS, type EvEnergyReport } from "@station/contracts";
import { Decimal, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DateRangePicker,
  EmptyState,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChartColumnBig, Users, Wrench } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  AVAILABILITY_BUCKETS,
  type AvailabilityBucket,
  availabilityChartOption,
  defaultEvRange,
  energyByDayChartOption,
  formatKwh,
  formatPercent,
  formatRate,
} from "../../../features/ev/ev-values.js";
import {
  useEvAvailabilityReport,
  useEvCustomerSpendReport,
  useEvEnergyReport,
  useEvOccupancyReport,
  useEvPermissions,
} from "../../../features/ev/queries.js";
import {
  evSearchShape,
  EvStationPage,
  type EvStationContext,
  Field,
  SessionWarnings,
  TableFrame,
  Th,
} from "../../../features/ev/shared.js";
import { ChartWithTable } from "../../../features/fuel/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import {
  formatDuration,
  marginTone,
  occupancyHeatmapOption,
  occupancyRows,
  occupiedHourRange,
} from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate } from "../../../lib/format.js";

const TABS = ["energy", "occupancy", "availability", "customers"] as const;
type TabKey = (typeof TABS)[number];
const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...evSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/sarj/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ReportsRoute,
});

/**
 * Şarj raporları (Prompt 19 web, `ev.report.read`; formüller `docs/domain/ev.md` §Raporlar): enerji / maliyet / marj (kWh /
 * gün grafiği, KDV hariç gelir, enerji maliyeti — alt sayaç birim maliyeti —, marj, alt sayaç kaybı, ödeme durumu kırılımı,
 * ortalama süreler, uyarılar), doluluk ısı haritası (hafta günü × saat), erişilebilirlik / arıza (kova kırılımı, bağlantı
 * kopukluğu, hata kodu tablosu) ve şarj müşterisinin market / kafe harcaması. Aralık oturumun BAŞLADIĞI İstanbul günüdür.
 * Her grafiğin yanında aynı veriyi veren bir tablo vardır.
 */
function ReportsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <EvStationPage
      title={t("menu.evReports")}
      description={t("ev.reports.description")}
      requirement="reports"
    >
      {(context) => <ReportsBody context={context} />}
    </EvStationPage>
  );
}

function ReportsBody({ context }: { context: EvStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const fallback = defaultEvRange(toIstanbulDate(new Date()));
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const tab: TabKey = search.tab ?? "energy";
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  const stationId = context.station.id;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("ev.reports.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
        <p className="text-xs text-muted-foreground">{t("ev.reports.rangeHint")}</p>
      </Card>
      <Tabs
        value={tab}
        onValueChange={(next) => setSearch({ tab: next === "energy" ? undefined : (next as TabKey) })}
      >
        <TabsList aria-label={t("ev.reports.tabsLabel")} className="flex-wrap">
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`ev.reports.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="energy" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "energy" ? <EnergyTab stationId={stationId} range={range} /> : null}
        </TabsContent>
        <TabsContent value="occupancy" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "occupancy" ? <OccupancyTab stationId={stationId} range={range} /> : null}
        </TabsContent>
        <TabsContent value="availability" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "availability" ? <AvailabilityTab stationId={stationId} range={range} /> : null}
        </TabsContent>
        <TabsContent value="customers" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "customers" ? <CustomersTab stationId={stationId} range={range} /> : null}
        </TabsContent>
      </Tabs>
    </>
  );
}

type Range = { from: string; to: string };

// ── Enerji / maliyet / marj ──────────────────────────────────────────────────
function EnergyTab({ stationId, range }: { stationId: string; range: Range }): ReactElement {
  const { t } = useTranslation();
  const report = useEvEnergyReport({ stationId, ...range });
  const option = useMemo(
    () =>
      report.data
        ? energyByDayChartOption(report.data.byDay, {
            energy: t("ev.reports.energy.energy"),
            revenue: t("ev.reports.energy.revenueNet"),
            cost: t("ev.reports.energy.energyCost"),
            margin: t("ev.reports.energy.margin"),
            energyAxis: t("ev.reports.energy.energyAxis"),
            moneyAxis: t("ev.reports.energy.moneyAxis"),
          })
        : null,
    [report.data, t],
  );
  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data || !option) return <SkeletonRows rows={6} />;
  const data: EvEnergyReport = report.data;
  const totals = data.totals;
  const cost = data.energyCost;

  return (
    <>
      <Card className="grid gap-3 p-4" data-testid="ev-energy-totals">
        <h2 className="text-base font-semibold">{t("ev.reports.energy.totalsTitle")}</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
          <Field label={t("ev.reports.energy.sessions")}>
            <span className="tabular text-lg">{formatCount(totals.sessionCount)}</span>
          </Field>
          <Field label={t("ev.reports.energy.energy")}>
            <span className="tabular text-lg">{formatKwh(totals.energyKwh)}</span>
          </Field>
          <Field label={t("ev.reports.energy.revenueNet")}>
            <MoneyText value={totals.revenueNet} className="text-lg" />
          </Field>
          <Field label={t("ev.reports.energy.energyCost")}>
            <MoneyText value={totals.energyCost} className="text-lg" />
          </Field>
          <Field label={t("ev.reports.energy.margin")}>
            <MoneyText value={totals.margin} colorize className="text-lg" />
          </Field>
          <Field label={t("ev.reports.energy.marginPercent")}>
            {totals.marginPercent === null ? (
              "—"
            ) : (
              <StatusBadge tone={marginTone(totals.marginPercent)} className="tabular">
                {formatPercent(totals.marginPercent, 2)}
              </StatusBadge>
            )}
          </Field>
          <Field label={t("ev.reports.energy.revenueGross")}>
            <MoneyText value={totals.revenueGross} />
          </Field>
          <Field label={t("ev.reports.energy.unpaid")}>
            <MoneyText value={totals.unpaidAmount} />
          </Field>
          <Field label={t("ev.reports.energy.partnerAmount")}>
            <MoneyText value={totals.partnerAmount} />
          </Field>
          <Field label={t("ev.reports.energy.covered")}>
            <span className="tabular">{formatKwh(totals.coveredKwh)}</span>
          </Field>
          <Field label={t("ev.reports.energy.avgSession")}>
            <span className="tabular">{minutesText(totals.avgSessionMinutes)}</span>
          </Field>
          <Field label={t("ev.reports.energy.avgChargingIdle")}>
            <span className="tabular">
              {minutesText(totals.avgChargingMinutes)} / {minutesText(totals.avgIdleMinutes)}
            </span>
          </Field>
        </dl>
      </Card>

      <Card className="grid gap-3 p-4" data-testid="ev-energy-cost">
        <h2 className="text-base font-semibold">{t("ev.reports.energy.costTitle")}</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
          <Field label={t("ev.reports.energy.costSource")}>
            <span className="grid gap-0.5">
              {t(`ev.reports.energy.source.${cost.source}`)}
              {cost.meterName ? (
                <span className="text-xs font-normal text-muted-foreground">{cost.meterName}</span>
              ) : null}
            </span>
          </Field>
          <Field label={t("ev.reports.energy.unitCost")}>{formatRate(cost.unitCost, "KWH")}</Field>
          <Field label={t("ev.reports.energy.meterConsumption")}>
            <span className="tabular">{formatKwh(cost.meterConsumptionKwh)}</span>
          </Field>
          <Field label={t("ev.reports.energy.sessionEnergy")}>
            <span className="tabular">{formatKwh(cost.sessionEnergyKwh)}</span>
          </Field>
          <Field label={t("ev.reports.energy.loss")}>
            <span className="tabular">{formatKwh(cost.lossKwh)}</span>
          </Field>
          <Field label={t("ev.reports.energy.lossPercent")}>
            <span className="tabular">{formatPercent(cost.lossPercent, 2)}</span>
          </Field>
        </dl>
        <p className="text-xs text-muted-foreground">
          {cost.source === "EV_METER"
            ? t("ev.reports.energy.costHintMeter")
            : cost.source === "STATION_METER"
              ? t("ev.reports.energy.costHintStation")
              : t("ev.reports.energy.costHintNone")}
        </p>
      </Card>

      {data.warnings.length > 0 ? (
        <Alert tone="warning" title={t("ev.reports.warnings")}>
          <SessionWarnings warnings={data.warnings} />
        </Alert>
      ) : null}

      {data.byDay.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<ChartColumnBig />}
          title={t("ev.reports.emptyTitle")}
          description={t("ev.reports.emptyDescription")}
        />
      ) : (
        <>
          <Card className="min-w-0 p-4">
            <h2 className="mb-2 text-base font-semibold">{t("ev.reports.energy.chartTitle")}</h2>
            <ChartWithTable
              ariaLabel={t("ev.reports.energy.chartAria", {
                from: formatDate(data.from),
                to: formatDate(data.to),
              })}
              option={option}
              height={340}
              tableLabel={t("ev.reports.energy.byDay")}
            >
              <EnergyRowsTable
                rows={data.byDay.map((row) => ({ ...row, label: formatDate(row.date), key: row.date }))}
                firstHeader={t("ev.reports.energy.day")}
                caption={t("ev.reports.energy.byDay")}
              />
            </ChartWithTable>
          </Card>
          <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
            <h2 className="text-base font-semibold">{t("ev.reports.energy.byCharger")}</h2>
            <div
              className="overflow-x-auto rounded-lg border"
              tabIndex={0}
              role="group"
              aria-label={t("ev.reports.energy.byCharger")}
            >
              <EnergyRowsTable
                rows={data.byCharger.map((row) => ({
                  ...row,
                  label: row.chargerName,
                  key: row.chargerId ?? row.chargerName,
                }))}
                firstHeader={t("ev.fields.charger")}
                caption={t("ev.reports.energy.byCharger")}
              />
            </div>
          </Card>
          <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
            <h2 className="text-base font-semibold">{t("ev.reports.energy.byPayment")}</h2>
            <TableFrame label={t("ev.reports.energy.byPayment")}>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("ev.fields.paymentStatus")}</Th>
                  <Th align="right">{t("ev.reports.energy.sessions")}</Th>
                  <Th align="right">{t("ev.reports.energy.energy")}</Th>
                  <Th align="right">{t("ev.fields.totalAmount")}</Th>
                </tr>
              </thead>
              <tbody>
                {data.byPaymentStatus.map((row) => (
                  <tr key={row.paymentStatus} className="border-t">
                    <th scope="row" className="px-3 py-2 text-left font-medium">
                      {EV_PAYMENT_STATUS_LABELS[row.paymentStatus]}
                    </th>
                    <td className="px-3 py-2 text-right tabular">{formatCount(row.sessionCount)}</td>
                    <td className="px-3 py-2 text-right tabular">{formatKwh(row.energyKwh)}</td>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={row.totalAmount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableFrame>
          </Card>
        </>
      )}
    </>
  );
}

function minutesText(value: string | null): string {
  return value === null ? "—" : formatDuration(Decimal.from(value).mul(60).round(0).toString());
}

function EnergyRowsTable({
  rows,
  firstHeader,
  caption,
}: {
  rows: Array<{
    key: string;
    label: string;
    sessionCount: number;
    energyKwh: string;
    revenueNet: string;
    energyCost: string;
    margin: string;
  }>;
  firstHeader: string;
  caption: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <table className="w-full text-sm">
      <caption className="sr-only">{caption}</caption>
      <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
        <tr>
          <Th>{firstHeader}</Th>
          <Th align="right">{t("ev.reports.energy.sessions")}</Th>
          <Th align="right">{t("ev.reports.energy.energy")}</Th>
          <Th align="right">{t("ev.reports.energy.revenueNet")}</Th>
          <Th align="right">{t("ev.reports.energy.energyCost")}</Th>
          <Th align="right">{t("ev.reports.energy.margin")}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-t">
            <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">
              {row.label}
            </th>
            <td className="px-3 py-2 text-right tabular">{formatCount(row.sessionCount)}</td>
            <td className="px-3 py-2 text-right tabular whitespace-nowrap">{formatKwh(row.energyKwh)}</td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={row.revenueNet} />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={row.energyCost} />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={row.margin} colorize />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Doluluk ──────────────────────────────────────────────────────────────────
function OccupancyTab({ stationId, range }: { stationId: string; range: Range }): ReactElement {
  const { t } = useTranslation();
  const report = useEvOccupancyReport({ stationId, ...range });
  const weekdays = useMemo(() => [1, 2, 3, 4, 5, 6, 7].map((day) => t(`ev.weekdaysLong.${day}`)), [t]);
  const option = useMemo(
    () =>
      report.data
        ? occupancyHeatmapOption(report.data, { weekdays, series: t("ev.reports.occupancy.series") })
        : null,
    [report.data, weekdays, t],
  );
  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data || !option) return <SkeletonRows rows={6} />;
  const data = report.data;
  const rows = occupancyRows(data);
  const hours = occupiedHourRange(rows);
  const visibleHours = Array.from(
    { length: hours.to - hours.from + 1 },
    (_unused, index) => hours.from + index,
  );
  const total = rows.reduce((sum, row) => sum + row.totalSeconds, 0);

  return (
    <>
      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">{t("ev.reports.occupancy.title")}</h2>
          <span className="text-sm text-muted-foreground">
            {t("ev.reports.occupancy.summary", {
              connectors: data.connectorCount,
              duration: formatDuration(total),
            })}
          </span>
        </div>
        <ChartWithTable
          ariaLabel={t("ev.reports.occupancy.aria", { from: formatDate(data.from), to: formatDate(data.to) })}
          option={option}
          height={360}
          tableLabel={t("ev.reports.occupancy.table")}
        >
          <table className="w-full text-xs">
            <caption className="sr-only">{t("ev.reports.occupancy.table")}</caption>
            <thead className="bg-muted/60 text-muted-foreground">
              <tr>
                <Th>{t("ev.reports.occupancy.weekday")}</Th>
                {visibleHours.map((hour) => (
                  <Th key={hour} align="right">
                    {String(hour).padStart(2, "0")}
                  </Th>
                ))}
                <Th align="right">{t("common.total")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.weekday} className="border-t">
                  <th scope="row" className="px-3 py-1.5 text-left font-medium">
                    {weekdays[row.weekday]}
                  </th>
                  {visibleHours.map((hour) => (
                    <td key={hour} className="px-2 py-1.5 text-right tabular">
                      %{Decimal.from(row.cells[hour]!.percent).formatTr(1)}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right tabular whitespace-nowrap">
                    {formatDuration(row.totalSeconds)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartWithTable>
        <p className="text-xs text-muted-foreground">{t("ev.reports.occupancy.hint")}</p>
      </Card>
      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
        <h2 className="text-base font-semibold">{t("ev.reports.occupancy.byConnector")}</h2>
        <TableFrame label={t("ev.reports.occupancy.byConnector")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("ev.fields.charger")}</Th>
              <Th>{t("ev.fields.connector")}</Th>
              <Th align="right">{t("ev.reports.occupancy.occupied")}</Th>
              <Th align="right">{t("ev.reports.occupancy.percent")}</Th>
            </tr>
          </thead>
          <tbody>
            {data.byConnector.map((row) => (
              <tr key={`${row.chargerId}-${row.connectorNo}`} className="border-t">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  {row.chargerName}
                </th>
                <td className="px-3 py-2">{t("ev.connector.no", { no: row.connectorNo })}</td>
                <td className="px-3 py-2 text-right tabular">{formatDuration(row.occupiedSeconds)}</td>
                <td className="px-3 py-2 text-right tabular">{formatPercent(row.occupancyPercent)}</td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </Card>
    </>
  );
}

// ── Erişilebilirlik / arıza ──────────────────────────────────────────────────
function AvailabilityTab({ stationId, range }: { stationId: string; range: Range }): ReactElement {
  const { t } = useTranslation();
  const report = useEvAvailabilityReport({ stationId, ...range });
  const buckets = useMemo(
    () =>
      Object.fromEntries(
        AVAILABILITY_BUCKETS.map((bucket) => [bucket, t(`ev.reports.availability.bucket.${bucket}`)]),
      ) as Record<AvailabilityBucket, string>,
    [t],
  );
  const option = useMemo(
    () =>
      report.data
        ? availabilityChartOption(report.data.connectors, {
            buckets,
            hoursAxis: t("ev.reports.availability.hoursAxis"),
            hoursUnit: t("ev.reports.availability.hoursUnit"),
            connector: (no) => t("ev.connector.no", { no }),
          })
        : null,
    [report.data, buckets, t],
  );
  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data || !option) return <SkeletonRows rows={6} />;
  const data = report.data;

  return (
    <>
      <Card className="grid gap-3 p-4" data-testid="ev-availability-totals">
        <h2 className="text-base font-semibold">{t("ev.reports.availability.totalsTitle")}</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Field label={t("ev.reports.availability.downtime")}>
            <span className="tabular text-lg">{formatDuration(data.totals.downtimeSeconds)}</span>
          </Field>
          <Field label={t("ev.reports.availability.fault")}>
            <span className="tabular text-lg">{formatDuration(data.totals.faultSeconds)}</span>
          </Field>
          <Field label={t("ev.reports.availability.offline")}>
            <span className="tabular text-lg">{formatDuration(data.totals.offlineSeconds)}</span>
          </Field>
          <Field label={t("ev.reports.availability.faultCount")}>
            <span className="tabular text-lg">{formatCount(data.totals.faultCount)}</span>
          </Field>
        </dl>
      </Card>
      {data.connectors.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<Wrench />}
          title={t("ev.reports.emptyTitle")}
          description={t("ev.reports.emptyDescription")}
        />
      ) : (
        <Card className="min-w-0 p-4">
          <h2 className="mb-2 text-base font-semibold">{t("ev.reports.availability.chartTitle")}</h2>
          <ChartWithTable
            ariaLabel={t("ev.reports.availability.chartAria", {
              from: formatDate(data.from),
              to: formatDate(data.to),
            })}
            option={option}
            height={Math.max(240, 96 + data.connectors.length * 48)}
            tableLabel={t("ev.reports.availability.table")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("ev.reports.availability.table")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("ev.fields.charger")}</Th>
                  {AVAILABILITY_BUCKETS.map((bucket) => (
                    <Th key={bucket} align="right">
                      {buckets[bucket]}
                    </Th>
                  ))}
                  <Th align="right">{t("ev.reports.availability.availabilityPercent")}</Th>
                </tr>
              </thead>
              <tbody>
                {data.connectors.map((row) => (
                  <tr key={`${row.chargerId}-${row.connectorNo}`} className="border-t">
                    <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">
                      {row.chargerName} · {t("ev.connector.no", { no: row.connectorNo })}
                    </th>
                    {AVAILABILITY_BUCKETS.map((bucket) => (
                      <td key={bucket} className="px-3 py-2 text-right tabular whitespace-nowrap">
                        {formatDuration(row.seconds[bucket])}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular">{formatPercent(row.availabilityPercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartWithTable>
        </Card>
      )}
      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
        <h2 className="text-base font-semibold">{t("ev.reports.availability.chargersTitle")}</h2>
        <TableFrame label={t("ev.reports.availability.chargersTitle")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("ev.fields.charger")}</Th>
              <Th align="right">{t("ev.reports.availability.offline")}</Th>
              <Th align="right">{t("ev.reports.availability.disconnects")}</Th>
              <Th>{t("common.status")}</Th>
            </tr>
          </thead>
          <tbody>
            {data.chargers.map((row) => (
              <tr key={row.chargerId} className="border-t">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  {row.chargerName}
                </th>
                <td className="px-3 py-2 text-right tabular">{formatDuration(row.offlineSeconds)}</td>
                <td className="px-3 py-2 text-right tabular">{formatCount(row.disconnectCount)}</td>
                <td className="px-3 py-2">
                  {row.neverConnected ? (
                    <StatusBadge tone="warning">{t("ev.reports.availability.neverConnected")}</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">{t("ev.reports.availability.connectedBefore")}</StatusBadge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </Card>
      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0" data-testid="ev-fault-codes">
        <h2 className="text-base font-semibold">{t("ev.reports.availability.faultsTitle")}</h2>
        {data.faults.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ev.reports.availability.noFaults")}</p>
        ) : (
          <TableFrame label={t("ev.reports.availability.faultsTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.fields.charger")}</Th>
                <Th>{t("ev.fields.connector")}</Th>
                <Th>{t("ev.reports.availability.errorCode")}</Th>
                <Th align="right">{t("ev.reports.availability.faultCount")}</Th>
                <Th align="right">{t("ev.fields.duration")}</Th>
              </tr>
            </thead>
            <tbody>
              {data.faults.map((row) => (
                <tr key={`${row.chargerId}-${row.connectorNo}-${row.errorCode}`} className="border-t">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {row.chargerName}
                  </th>
                  <td className="px-3 py-2">
                    {row.connectorNo === 0
                      ? t("ev.commands.wholeUnit")
                      : t("ev.connector.no", { no: row.connectorNo })}
                  </td>
                  <td className="px-3 py-2 font-mono">{row.errorCode}</td>
                  <td className="px-3 py-2 text-right tabular">{formatCount(row.count)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatDuration(row.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>
    </>
  );
}

// ── Müşteri harcaması ────────────────────────────────────────────────────────
function CustomersTab({ stationId, range }: { stationId: string; range: Range }): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  const report = useEvCustomerSpendReport({ stationId, ...range });
  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const data = report.data;
  const totals = data.totals;
  return (
    <>
      <Card className="grid gap-3 p-4" data-testid="ev-customer-totals">
        <h2 className="text-base font-semibold">{t("ev.reports.customers.totalsTitle")}</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Field label={t("ev.reports.customers.customers")}>
            <span className="tabular text-lg">{formatCount(totals.customerCount)}</span>
          </Field>
          <Field label={t("ev.reports.energy.sessions")}>
            <span className="tabular text-lg">{formatCount(totals.sessionCount)}</span>
          </Field>
          <Field label={t("ev.reports.customers.evAmount")}>
            <MoneyText value={totals.evAmount} className="text-lg" />
          </Field>
          <Field label={t("ev.reports.customers.sameDay")}>
            <MoneyText value={totals.sameDayAmount} className="text-lg" />
          </Field>
        </dl>
        <p className="text-xs text-muted-foreground">{t("ev.reports.customers.hint")}</p>
      </Card>
      {data.customers.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<Users />}
          title={t("ev.reports.customers.emptyTitle")}
          description={t("ev.reports.customers.emptyDescription")}
        />
      ) : (
        <TableFrame label={t("ev.reports.customers.caption")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("ev.reports.customers.customer")}</Th>
              <Th align="right">{t("ev.reports.energy.sessions")}</Th>
              <Th align="right">{t("ev.reports.energy.energy")}</Th>
              <Th align="right">{t("ev.reports.customers.evAmount")}</Th>
              <Th align="right">{t("ev.reports.customers.during")}</Th>
              <Th align="right">{t("ev.reports.customers.sameDay")}</Th>
              <Th align="right">{t("ev.reports.customers.market")}</Th>
              <Th align="right">{t("ev.reports.customers.cafe")}</Th>
              <Th align="right">{t("ev.reports.customers.other")}</Th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((row) => (
              <tr key={row.customerProfileId} className="border-t">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  <span className="grid gap-0.5">
                    {permissions.canOpenCustomer ? (
                      <Link
                        to="/musteriler/$id"
                        params={{ id: row.customerProfileId }}
                        className="underline underline-offset-4"
                      >
                        {row.customerName ?? row.customerCode}
                      </Link>
                    ) : (
                      (row.customerName ?? row.customerCode)
                    )}
                    <span className="font-mono text-xs font-normal text-muted-foreground">
                      {row.customerCode}
                    </span>
                  </span>
                </th>
                <td className="px-3 py-2 text-right tabular">{formatCount(row.sessionCount)}</td>
                <td className="px-3 py-2 text-right tabular whitespace-nowrap">{formatKwh(row.energyKwh)}</td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.evAmount} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.duringSessionAmount} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.sameDayAmount} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.marketAmount} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.cafeAmount} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.otherAmount} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      )}
    </>
  );
}
